import Vue from 'vue'
import Vuex from 'vuex'
import { env } from '../config'
import { getTokenPayload } from '@/resources'
import { client } from '@/resources'
import UserResource from '@/resources/user'
import PageResource from '@/resources/page'
import LocationResource from '@/resources/location'
import { destroyLucraClient } from '@/util/lucra/client'

Vue.use(Vuex)

export const localPermissions = localStorage.getItem('permissions') ? JSON.parse(localStorage.getItem('permissions')) : false

export default new Vuex.Store({
    state: {
        instance: 'http://localhost:8080',
        serverConfig: {},
        token: localStorage.getItem('token') || null,
        permissions: localPermissions || null,
        userResource: null,
        isApexClub: false,
        creditsBalance: 0,
        pageResources: [],
        locationResources: [],
        locationNearest: null,
        locationSelected: null,
        branding: {},
        logoURL: null,
        logoWidth: 200
    },
    getters: {
        serverConfig(state) {
            return state.serverConfig
        },
        token(state) {
            return state.token
        },
        tokenPayload(state) {
            return getTokenPayload(state.token)
        },
        userId(state) {
            let payload = getTokenPayload(state.token)
            if (payload) {
                return payload['user_id']
            }
            return undefined
        },
        userResource(state) {
            return state.userResource
        },
        permissions(state) {
            return state.permissions
        },
        permissionsModels(state) {
            return state.permissions?.permissions || []
        },
        isStaff(state) {
            if (state.permissions && !!state.permissions['is_staff']) {
                return true
            }

            return false
        },
        isSuperuser(state) {
            if (state.permissions && !!state.permissions['is_superuser']) {
                return true
            }

            return false
        },
        isApexClub(state) {
            return state.isApexClub
        },
        creditsBalance(state) {
            return state.creditsBalance
        },
        pageResources(state) {
            return state.pageResources
        },
        pageResourceNavLinks(state) {
            return state.pageResources.filter((resource) => !!resource.attributes.menu_name)
        },
        locationResources(state) {
            return state.locationResources
        },
        locationNearest(state) {
            return state.locationNearest
        },
        locationSelected(state) {
            return state.locationSelected
        },
        branding(state) {
            return state.branding
        },
        logoURL(state) {
            return state.logoURL
        },
        logoWidth(state) {
            return state.logoWidth
        },
        currencySymbol(state) {
            return state.locationSelected?.attributes?.currency_symbol || '$'
        }
    },
    mutations: {
        setToken(state, token) {
            state.token = token
            localStorage.setItem('token', token)
        },
        setUserResource(state, userResource) {
            const previousId = state.userResource ? state.userResource.id : null
            const nextId = userResource ? userResource.id : null
            if (previousId && nextId && previousId !== nextId) {
                // Switching which ShiftOS user is signed in in this browser. Drop any Lucra
                // session tied to the previous person so the new user gets their own login
                // instead of silently inheriting the old one.
                destroyLucraClient()
            }
            state.userResource = userResource
        },
        setPermissions(state, permissionsObj) {
            state.permissions = permissionsObj
            localStorage.setItem('permissions', JSON.stringify(permissionsObj))
        },
        setServerConfig(state, config) {
            state.serverConfig = {
                'API_BASE_URL': env.API_BASE_URL,
                'API_LOCATION_GROUP_ID': env.API_LOCATION_GROUP_ID,
                ...config
            }
        },
        logout(state) {
            state.token = null
            state.permissions = null
            state.userResource = null
            destroyLucraClient()
            const beforeAdminPlannerZoom = localStorage.getItem('adminPlannerZoom')
            const beforeAdminRaceControlZoom = localStorage.getItem('adminRaceControlZoom')
            localStorage.clear()

            if (beforeAdminPlannerZoom) {
                localStorage.setItem('adminPlannerZoom', beforeAdminPlannerZoom)
            }

            if (beforeAdminRaceControlZoom) {
                localStorage.setItem('adminRaceControlZoom', beforeAdminRaceControlZoom)
            }
        },
        isApexClub(state, value) {
            state.isApexClub = value
        },
        creditsBalance(state, value) {
            state.creditsBalance = value
        },
        setPageResources(state, value) {
            state.pageResources = value
        },
        setLocationResources(state, locations) {
            state.locationResources = locations
        },
        setLocationNearest(state, location) {
            state.locationNearest = location
        },
        setLocationSelected(state, location) {
            state.locationSelected = location
        },
        setBranding(state, brandingData) {
            state.branding = brandingData
        },
        setLogoData(state, logoObj) {
            state.logoURL = logoObj.url
            state.logoWidth = logoObj.width
        }
    },
    actions: {
        async appInitialize(context) {
            return await Promise.all([
                context.dispatch('getServerConfig'),
                context.dispatch('getUserResource'),
                context.dispatch('getUserPermissions'),
                context.dispatch('getLocationData'),
                context.dispatch('getPageResources')
            ])
        },
        async getServerConfig(context) {
            const result = await client.options()
            context.commit('setServerConfig', result.data)
        },
        async login(context, creds) {
            let result = await UserResource.login(creds.username, creds.password)
            context.commit('setToken', result.token)
            await context.dispatch('appInitialize')
            return result
        },
        async getUserPermissions(context) {
            if (!context.getters.userId) {
                return null
            }

            if (context.state.permissions) {
                return await Promise.resolve(context.state.permissions)
            }

            let user = await UserResource.detail(context.getters.userId)
            let perms = await user.getPermissions()
            context.commit('setPermissions', perms)
            return perms
        },
        async getUserResource(context) {
            if (context.getters.userId) {
                try {
                    const userResource = await UserResource.detail(context.getters.userId)
                    context.commit('setUserResource', userResource)
                    context.commit('isApexClub', userResource.attributes.credits_subscription_in_good_standing)
                    context.commit('creditsBalance', userResource.attributes.credits_balance)
                    return userResource
                } catch (e) {
                    if (e.response && e.response.status == 401) {
                        window.location.href = '/login?errorCode=tokenExpired'
                    } else {
                        throw e
                    }
                }
            }
        },
        async getPageResources(context) {
            const pageResources = await PageResource.all()
            context.commit('setPageResources', pageResources)
            return pageResources
        },
        async getLocationData(context) {
            const [locationsAll, locationNearest] = await Promise.all([
                LocationResource.all(),
                LocationResource.nearest()
            ])

            const locationSelected = locationNearest

            context.commit('setLocationResources', locationsAll)
            context.commit('setLocationNearest', locationNearest)
            context.commit('setLocationSelected', locationSelected)

            if (locationSelected?.attributes.branding) {
                context.commit('setBranding', {
                    ...locationSelected.attributes.branding,
                    design_logo_image_url: locationSelected.attributes.branding.readonly_values.design_logo_image_url
                })
            }
        }
    },
    modules: {},
})
